import { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Chip, Avatar, Typography,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Menu, MenuItem,
  Checkbox, ListItemText, Divider, LinearProgress,
  Snackbar, Alert, TextField, InputAdornment,
  FormControl, InputLabel, Select,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import HexagonIcon from "@mui/icons-material/Hexagon";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MemoryIcon from "@mui/icons-material/Memory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

// ── Theme ─────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#0284c7" },
    secondary: { main: "#10b981" },
    background: { default: "#f5f7fa", paper: "#ffffff" },
    text: { primary: "#1e293b", secondary: "#475569" },
  },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    h5: { fontWeight: 800, letterSpacing: "-0.5px" },
    subtitle2: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgb(0 0 0/0.06),0 1px 2px -1px rgb(0 0 0/0.06)",
          border: "1px solid #e2e8f0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#f8fafc", color: "#475569", fontSize: 11, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.06em",
          borderBottom: "1px solid #e2e8f0", padding: "10px 18px", whiteSpace: "nowrap",
        },
        body: {
          fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9",
          padding: "14px 18px", verticalAlign: "middle",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover td": { backgroundColor: "#f8faff" },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 20, height: 7, backgroundColor: "#e5e7eb" },
        bar: { borderRadius: 20 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10, backgroundColor: alpha("#0284c7", 0.04),
            "& fieldset": { borderColor: alpha("#0284c7", 0.2) },
            "&:hover fieldset": { borderColor: "#0284c7" },
            "&.Mui-focused fieldset": { borderColor: "#0284c7", borderWidth: 1.5 },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: 8, fontSize: 13 },
      },
    },
  },
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function efficiencyBadge(pct) {
  if (pct > 100) return { label: "Excellent", color: "success" };
  if (pct > 70) return { label: "Good", color: "warning" };
  return { label: "Poor", color: "error" };
}

/** Format YYYY-MM-DD → "Mar 01, 2026" for the active range label */
function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

// ── Aggregate types matching the API ─────────────────────────────────────────
export const AGGREGATE_TYPES = [
  "Pod", "Namespace", "Cluster", "Node",
  "Service", "Controller", "Container",
  "Deployment", "DaemonSet",
];

const COL_DEFS = [
  { key: "name", label: "Name" },
  { key: "cpu", label: "CPU Usage" },
  { key: "memory", label: "Memory Usage" },
  { key: "cost", label: "Total Cost" },
  { key: "efficiency", label: "Efficiency" },
];

/**
 * Transform OpenCost namespace data into flat array.
 */
function transformNamespaceData(data) {
  if (!data || !data.data || !Array.isArray(data.data)) return [];

  return data.data.map(item => {
    if (!item || typeof item !== "object") return null;

    const name = item.name || item.properties?.namespace || "unknown";
    const ramGB = item.ramBytes ? item.ramBytes / (1024 ** 3) : 0;

    return {
      id: name,
      name,
      cpu: item.cpuCores || 0,
      memory: ramGB,
      cost: item.totalCost || 0,
      efficiency: item.totalEfficiency ? (item.totalEfficiency * 100).toFixed(2) : 0,
      cpuCoreRequestAverage: item.cpuCoreRequestAverage || 0,
      cpuCoreLimitAverage: item.cpuCoreLimitAverage || 0,
      cpuCoreUsageAverage: item.cpuCoreUsageAverage || 0,
      ramByteRequestAverage: item.ramByteRequestAverage || 0,
      ramByteLimitAverage: item.ramByteLimitAverage || 0,
      ramByteUsageAverage: item.ramByteUsageAverage || 0,
      totalCost: item.totalCost || 0,
      totalEfficiency: item.totalEfficiency || 0,
      cpuEfficiency: item.cpuEfficiency || 0,
      ramEfficiency: item.ramEfficiency || 0,
      gpuEfficiency: item.gpuEfficiency || 0,
      cpuCost: item.cpuCost || 0,
      ramCost: item.ramCost || 0,
      start: item.start || "",
      end: item.end || "",
    };
  }).filter(Boolean);
}

// ── Main Component ────────────────────────────────────────────────────────────
/**
 * EKSNamespace
 *
 * Props:
 *   namespaceData     {object|null}  - OpenCost API response from parent
 *   aggregate         {string}       - Current aggregate type (e.g. "Namespace")
 *   onAggregateChange {function}     - Called immediately when aggregate changes
 *   startMonthDate    {string}       - YYYY-MM-DD — owned by ConnectK8sCluster
 *   endMonthDate      {string}       - YYYY-MM-DD — owned by ConnectK8sCluster
 *   onDateChange      {function}     - (startDate, endDate) => void
 *                                     Called only when user clicks "Apply".
 *                                     Parent updates its state and re-fetches.
 */
export default function EKSNamespace({
  namespaceData = null,
  aggregate = "Namespace",
  onAggregateChange = null,
  startMonthDate = "",
  endMonthDate = "",
  onDateChange = null,
  clusterLabel = null,
}) {
  const RAW_DATA = namespaceData ? transformNamespaceData(namespaceData) : [];

  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState(RAW_DATA);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [colAnchor, setColAnchor] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const [visibleCols, setVisibleCols] = useState({
    name: true, cpu: true, memory: true, cost: true, efficiency: true,
  });

  // Local draft state for date inputs.
  // User edits these freely — no fetch is triggered until "Apply" is clicked.
  const [localStart, setLocalStart] = useState(startMonthDate);
  const [localEnd, setLocalEnd] = useState(endMonthDate);

  // Sync local drafts if parent resets dates (e.g. on cluster switch)
  useEffect(() => { setLocalStart(startMonthDate); }, [startMonthDate]);
  useEffect(() => { setLocalEnd(endMonthDate); }, [endMonthDate]);

  // Reset table when incoming data changes
  useEffect(() => {
    const transformed = namespaceData ? transformNamespaceData(namespaceData) : [];
    setFiltered(transformed);
    setSearch("");
  }, [namespaceData]);

  // Search filter
  useEffect(() => {
    const t = search.toLowerCase();
    const base = namespaceData ? transformNamespaceData(namespaceData) : [];
    setFiltered(t ? base.filter(ns => ns.name.toLowerCase().includes(t)) : base);
  }, [search, namespaceData]);

  const handleAggregateChange = (e) => {
    if (onAggregateChange) onAggregateChange(e.target.value);
  };

  /**
   * Apply button — sends validated dates up to parent which re-fetches.
   */
  const handleApply = () => {
    if (!localStart || !localEnd) return;
    if (onDateChange) onDateChange(localStart, localEnd);
  };

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    const vals = {
      name: [a.name, b.name],
      cpu: [a.cpuCoreUsageAverage, b.cpuCoreUsageAverage],
      memory: [a.ramByteUsageAverage, b.ramByteUsageAverage],
      cost: [a.totalCost, b.totalCost],
      efficiency: [a.totalEfficiency, b.totalEfficiency],
    };
    const [av, bv] = vals[sortCol];
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  function toggleCol(key) {
    setVisibleCols(p => ({ ...p, [key]: !p[key] }));
  }

  function exportCSV() {
    const header = "Name,CPU Usage (cores),Memory Usage (GB),Total Cost,Efficiency";
    const rows = filtered.map(ns =>
      [ns.name, ns.cpuCoreUsageAverage,
      (ns.ramByteUsageAverage / 1024 ** 3).toFixed(2),
      ns.totalCost.toFixed(4),
      (ns.totalEfficiency * 100).toFixed(1) + "%"].join(",")
    );
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob), download: "namespace_data.csv",
    });
    a.click();
  }

  // Summary stats
  const totalCost = filtered.reduce((s, ns) => s + ns.totalCost, 0);
  const avgCPU = filtered.length
    ? filtered.reduce((s, ns) => {
      const pct = ns.cpuCoreRequestAverage > 0
        ? (ns.cpuCoreUsageAverage / ns.cpuCoreRequestAverage) * 100
        : ns.cpuCoreUsageAverage * 100;
      return s + pct;
    }, 0) / filtered.length
    : 0;
  const avgEfficiency = filtered.length
    ? filtered.reduce((s, ns) => s + ns.totalEfficiency * 100, 0) / filtered.length
    : 0;

  const statCards = [
    { label: "Total Items", value: filtered.length, icon: <HexagonIcon />, color: "#0284c7", bg: "#e0f2fe" },
    { label: "Total Cost", value: `$${totalCost.toFixed(2)}`, icon: <AttachMoneyIcon />, color: "#10b981", bg: "#d1fae5" },
    { label: "Avg CPU Usage", value: `${avgCPU.toFixed(1)}%`, icon: <MemoryIcon />, color: "#d97706", bg: "#fef3c7" },
    { label: "Avg Efficiency", value: `${avgEfficiency.toFixed(1)}%`, icon: <TrendingUpIcon />, color: "#0369a1", bg: "#bae6fd" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", p: { xs: 2, md: 3 } }}>

        {/* ── Header Card ───────────────────────────────────────────────────── */}
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: "20px 24px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: alpha("#0284c7", 0.1), color: "primary.main", width: 46, height: 46, borderRadius: "12px", "& svg": { fontSize: 22 } }}>
                  <HexagonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ lineHeight: 1.2, color: "text.primary" }}>
                    {aggregate}s {clusterLabel && ` — ${clusterLabel}`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                      Resource usage & cost analysis
                    </Typography>
                    <Chip
                      label={`${RAW_DATA.length} ${aggregate.toLowerCase()}${RAW_DATA.length !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: alpha("#0284c8", 0.1), color: "primary.main" }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Aggregate selector */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="ns-aggregate-label" sx={{ fontSize: 13 }}>Aggregate By</InputLabel>
                <Select
                  labelId="ns-aggregate-label"
                  value={aggregate}
                  label="Aggregate By"
                  onChange={handleAggregateChange}
                  sx={{
                    fontSize: 13, borderRadius: "10px",
                    backgroundColor: "rgba(2,132,199,0.04)",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(2,132,199,0.2)" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0284c7" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0284c7", borderWidth: 1.5 },
                  }}
                >
                  {AGGREGATE_TYPES.map(type => (
                    <MenuItem key={type} value={type} sx={{ fontSize: 13 }}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* ── Column Toggle Menu ────────────────────────────────────────────── */}
        <Menu
          anchorEl={colAnchor}
          open={Boolean(colAnchor)}
          onClose={() => setColAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 200, mt: 0.5, border: "1px solid #e2e8f0" } }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Toggle Columns
            </Typography>
          </Box>
          <Divider />
          {COL_DEFS.map(col => (
            <MenuItem key={col.key} dense onClick={() => toggleCol(col.key)} sx={{ gap: 0.5 }}>
              <Checkbox size="small" checked={visibleCols[col.key]} sx={{ p: 0.5, mr: 0.5, color: "primary.main" }} />
              <ListItemText primary={col.label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          ))}
        </Menu>

        {/* ── Summary Cards ─────────────────────────────────────────────────── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" }, gap: 2, mb: 3 }}>
          {statCards.map(card => (
            <Card key={card.label}>
              <CardContent sx={{ p: "18px 20px !important" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: 11.5 }}>{card.label}</Typography>
                    <Typography variant="h5" sx={{ mt: 0.5, fontSize: 26, color: "text.primary" }}>{card.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: card.bg, color: card.color, width: 42, height: 42, borderRadius: "10px", "& svg": { fontSize: 20 } }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* ── Date Range Toolbar Band ────────────────────────────────────────
            Layout:  [cal icon]  [active range label]  |  [spacer]  [From input] → [To input] [Apply]
            - Left side: always shows the currently applied date range (read-only, from parent props)
            - Right side: local draft inputs + Apply button (no fetch until Apply is clicked)
            - Kept slim (py: 1) so it doesn't compete visually with the summary cards above
        ─────────────────────────────────────────────────────────────────────── */}
        {/* ── Professional Responsive Toolbar ─────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            p: { xs: 2, sm: 2.5 },
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 2,
              alignItems: { lg: "flex-end" },
            }}
          >
            {/* Search Bar */}
            <Box sx={{ flex: { lg: 1 }, width: "100%" }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.disabled", mb: 0.5 }}>
                Search
              </Typography>
              <TextField
                size="small"
                placeholder="Search namespaces..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: 13, height: 34 },
                }}
              />
            </Box>

            {/* Date Filters + Apply + Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 2, lg: 1.5 },
                alignItems: { lg: "flex-end" },
                width: { xs: "100%", lg: "auto" },
              }}
            >
              {/* From → To + Apply */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-end",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                {/* From */}
                <Box sx={{ flex: { xs: 1, lg: "none" }, minWidth: 130 }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.disabled", mb: 0.5 }}>
                    From
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={localStart}
                    onChange={e => setLocalStart(e.target.value)}
                    fullWidth
                    InputProps={{ sx: { height: 34, fontSize: 13 } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                      },
                    }}
                  />
                </Box>

                {/* Arrow */}
                <Typography
                  sx={{
                    fontSize: 16,
                    color: "text.disabled",
                    pb: "7px",
                    display: { xs: "none", sm: "block" },
                    lineHeight: 1,
                  }}
                >
                  →
                </Typography>

                {/* To */}
                <Box sx={{ flex: { xs: 1, lg: "none" }, minWidth: 130 }}>
                  <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.disabled", mb: 0.5 }}>
                    To
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={localEnd}
                    onChange={e => setLocalEnd(e.target.value)}
                    fullWidth
                    InputProps={{ sx: { height: 34, fontSize: 13 } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        backgroundColor: "#ffffff",
                      },
                    }}
                  />
                </Box>

                {/* Apply */}
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  disabled={!localStart || !localEnd}
                  onClick={handleApply}
                  sx={{
                    height: 34,
                    px: 3,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: "6px",
                    textTransform: "none",
                    minWidth: 90,
                    flexShrink: 0,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Apply
                </Button>
              </Box>

              {/* Columns & Export Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexShrink: 0,
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
                  onClick={e => setColAnchor(e.currentTarget)}
                  sx={{
                    height: 34,
                    px: 2,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: "6px",
                    textTransform: "none",
                    flex: { xs: 1, lg: "none" },
                  }}
                >
                  Columns
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={exportCSV}
                  sx={{
                    height: 34,
                    px: 2,
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: "6px",
                    textTransform: "none",
                    flex: { xs: 1, lg: "none" },
                  }}
                >
                  Export CSV
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>


        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {COL_DEFS.filter(c => visibleCols[c.key]).map(col => (
                    <TableCell key={col.key}>
                      <TableSortLabel
                        active={sortCol === col.key}
                        direction={sortCol === col.key ? sortDir : "asc"}
                        onClick={() => handleSort(col.key)}
                        sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { opacity: sortCol === col.key ? 1 : 0.3 } }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      {RAW_DATA.length === 0
                        ? "No namespace data available. Configure OpenCost to see metrics."
                        : "No namespaces match your search."}
                    </TableCell>
                  </TableRow>
                ) : sorted.map((ns, idx) => {
                  const cpuPct = ns.cpuCoreRequestAverage > 0
                    ? (ns.cpuCoreUsageAverage / ns.cpuCoreRequestAverage) * 100
                    : ns.cpuCoreUsageAverage * 100;
                  const memPct = ns.ramByteRequestAverage > 0
                    ? (ns.ramByteUsageAverage / ns.ramByteRequestAverage) * 100
                    : (ns.ramByteUsageAverage / 1024 ** 3) / 10 * 100;
                  const effPct = ns.totalEfficiency * 100;
                  const cpuEffPct = ns.cpuEfficiency * 100;
                  const ramEffPct = ns.ramEfficiency * 100;
                  const gpuEffPct = ns.gpuEfficiency * 100;
                  const memGB = (ns.ramByteUsageAverage / 1024 ** 3).toFixed(2);
                  const memReqGB = (ns.ramByteRequestAverage / 1024 ** 3).toFixed(2);
                  const memLimGB = (ns.ramByteLimitAverage / 1024 ** 3).toFixed(2);
                  const { label: effLabel, color: effColor } = efficiencyBadge(effPct);

                  return (
                    <TableRow key={`${ns.name}-${idx}`}>
                      {visibleCols.name && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: alpha("#0284c7", 0.08), color: "primary.main", width: 36, height: 36, borderRadius: "9px", "& svg": { fontSize: 17 } }}>
                              <HexagonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{ns.name}</Typography>
                              <Typography variant="caption" color="text.secondary">Active</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.cpu && (
                        <TableCell sx={{ minWidth: 200 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>{cpuPct.toFixed(1)}%</Typography>
                            <Typography variant="caption" color="text.secondary">{ns.cpuCoreUsageAverage.toFixed(3)} cores</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(cpuPct, 100)} sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#3b82f6" } }} />
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Req: {ns.cpuCoreRequestAverage}</Typography>
                            <Typography variant="caption" color="text.secondary">Limit: {ns.cpuCoreLimitAverage}</Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.memory && (
                        <TableCell sx={{ minWidth: 200 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>{memPct.toFixed(1)}%</Typography>
                            <Typography variant="caption" color="text.secondary">{memGB} GB</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(memPct, 100)} sx={{ "& .MuiLinearProgress-bar": { bgcolor: "#22c55e" } }} />
                          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">Req: {memReqGB} GB</Typography>
                            <Typography variant="caption" color="text.secondary">Limit: {memLimGB} GB</Typography>
                          </Box>
                        </TableCell>
                      )}
                      {visibleCols.cost && (
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontSize: 13 }}>${ns.totalCost.toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>CPU: ${ns.cpuCost.toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">RAM: ${ns.ramCost.toFixed(2)}</Typography>
                        </TableCell>
                      )}
                      {visibleCols.efficiency && (
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{"Total: "}</Typography>
                            <Typography variant="subtitle2" sx={{ fontSize: 13 }}>{effPct.toFixed(1)}</Typography>
                            {/* <Chip label={effLabel} size="small" color={effColor} sx={{ height: 18, fontSize: 10, fontWeight: 700 }} /> */}
                          </Box>
                          <Box sx={{ borderTop: "1px solid #f1f5f9", pt: 0.5 }}>
                            {[{ label: "CPU", val: cpuEffPct }, { label: "RAM", val: ramEffPct }, { label: "GPU", val: gpuEffPct }].map(row => (
                              <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">{row.label}:</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>{row.val.toFixed(1)}%</Typography>
                              </Box>
                            ))}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f1f5f9", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{sorted.length}</strong> of <strong>{RAW_DATA.length}</strong> {aggregate.toLowerCase()}{RAW_DATA.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        </Card>

        <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity="success" variant="filled" sx={{ fontSize: 13, borderRadius: 2 }} onClose={() => setSnack(s => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}